import React, { createContext, useContext, useEffect, useState } from "react";
import { getMasterData } from "../../questionnaire/api/athena";
import { normalizeMasterItems } from "../../questionnaire/utils/masterData";

export interface MasterOption {
  code: string;
  name: string;
  raceid: string;
  id: string;
  specialty?: string;
}

export interface MasterData {
  preferredPronouns?: MasterOption[];
  sexualOrientation?: MasterOption[];
  relationshipToPatient?: MasterOption[];
  emergencyContactRelationship?: MasterOption[];
  maritalStatus?: MasterOption[];
  ethnicity?: MasterOption[];
  language?: MasterOption[];
  occupation?: MasterOption[];
  genderIdentity?: MasterOption[];
  race?: MasterOption[];
  assignedSexAtBirth?: MasterOption[];
  insurancePackages?: MasterOption[];
  providers?: MasterOption[];
  raceId?: string;
}

interface MasterDataContextType {
  data: MasterData | null;
  loading: boolean;
  error: string | null;
}

const MasterDataContext = createContext<MasterDataContextType | undefined>(
  undefined
);

const MASTER_DATA_TYPES = [
  "preferredPronouns",
  "sexualOrientation",
  "relationshipToPatient",
  "emergencyContactRelationship",
  "maritalStatus",
  "ethnicity",
  "language",
  "occupation",
  "genderIdentity",
  "race",
  "assignedSexAtBirth",
  "insurancePackages",
  "providers",
].join(",");

export const MasterDataProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [data, setData] = useState<MasterData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const raw = await getMasterData(MASTER_DATA_TYPES);
 
        if (cancelled) return;
 
        const normalized: MasterData = {
          preferredPronouns: normalizeMasterItems(raw?.preferredPronouns),
          sexualOrientation: normalizeMasterItems(raw?.sexualOrientation),
          relationshipToPatient: normalizeMasterItems(raw?.relationshipToPatient, {
            codeKeys: ["code", "id"],
            nameKeys: ["patientRelationshipToPerson", "name"],
          }),
          emergencyContactRelationship: normalizeMasterItems(raw?.emergencyContactRelationship),
          maritalStatus: normalizeMasterItems(raw?.maritalStatus),
          ethnicity: normalizeMasterItems(raw?.ethnicity, {
            codeKeys: ["code", "id", "ethnicityId"],
          }),
          language: normalizeMasterItems(raw?.language, {
            codeKeys: ["code", "id", "languageCode"],
          }),
          occupation: normalizeMasterItems(raw?.occupation, {
            codeKeys: ["code", "id", "occupationCode"],
          }),
          genderIdentity: normalizeMasterItems(raw?.genderIdentity, {
            codeKeys: ["genderIdentityId", "id"],
            nameKeys: ["genderIdentity", "name"],
          }),
          race: normalizeMasterItems(raw?.race, {
            codeKeys: ["raceId", "code", "id"],
            nameKeys: ["name", "description", "raceId"],
          }),
          assignedSexAtBirth: normalizeMasterItems(raw?.assignedSexAtBirth),
          insurancePackages: normalizeMasterItems(raw?.insurancePackages, {
            codeKeys: ["insurancePackageId", "insurancepackageid", "code", "id"],
            nameKeys: ["name", "description", "insurancePackageId", "insurancepackageid"],
          }),
          providers: normalizeMasterItems(raw?.providers, {
            codeKeys: ["providerId", "id"],
            nameKeys: ["displayName", "name"],
            specialtyKeys: ["specialty"],
          }),
        };


        setData(normalized);
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load master data", err);
          setError("Unable to load reference data. Some options may be unavailable.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  const value: MasterDataContextType = {
    data,
    loading,
    error,
  };

  return (
    <MasterDataContext.Provider value={value}>
      {children}
    </MasterDataContext.Provider>
  );
};

export const useMasterData = (): MasterDataContextType => {
  const ctx = useContext(MasterDataContext);
  if (!ctx) {
    throw new Error("useMasterData must be used within a MasterDataProvider");
  }
  return ctx;
};
